<template lang="pug">
div
  h5.font-medium 加余额 
    span.text-primary.ml-2 当前：{{ $utils.currency(user?.money, user?.balance_currency || 'cny') }} 
    span.ml-2.font-bold.text-green-600(v-if="showPreview && addMoneyOpt.amount") 新余额：{{ newBalance }}
  div(class="flex items-start gap-2")
    div(class="flex-1 sm:max-w-[50%]")
      div.flex.flex-col.gap-2
        div.w-full
          Input.border-none.h-8(class="bg-[var(--background-color-pure)]" type="number" v-model="addMoneyOpt.amount" placeholder="加多少余额？（负数为扣除）")
        div.w-full(v-if="addMoneyOpt.is_payment")
          div.flex.gap-1
            Input.border-none.h-8(class="bg-[var(--background-color-pure)]" type="number" v-model="addMoneyOpt.transfer_amount" placeholder="实际转账多少？")
            //- client-only
            Select(v-model="addMoneyOpt.currency" class="w-20 h-8 w-auto")
              SelectTrigger.h-8(class="bg-[var(--background-color-pure)]")
                SelectValue(placeholder="币")
              SelectContent
                SelectItem(value="eur") EUR
                SelectItem(value="usd") USD
        div.w-full(v-if="!addMoneyOpt.is_payment")
          //- client-only(v-if="!showCustomReason")
          template(v-if="!showCustomReason")
            Select(v-model="selectedReason" :disabled='addMoneyOpt.is_payment')
              SelectTrigger.h-8(class="bg-[var(--background-color-pure)]")
                SelectValue(placeholder="选择理由")
              SelectContent
                SelectItem(value="回家服务退款 Refund:HUIJIA") 回家服务退款
                SelectItem(value="云游服务退款 Refund:YUNYOU") 云游服务退款
                SelectItem(value="USDT付款 USDT") USDT付款
                SelectItem(value="修正余额 Adjustment") 修正余额
                SelectItem(value="提前开通 Pay Later") 提前开通
                SelectItem(value="加购回家服务流量 Extra:HUIJIA") 加购回家流量
                SelectItem(value="custom") 自定义理由
          div.flex.gap-1(v-if="showCustomReason && !addMoneyOpt.is_payment")
            Input.border-none.h-8.flex-1(
              ref="customReasonInput"
              class="bg-[var(--background-color-pure)]" 
              v-model="addMoneyOpt.reason" 
              placeholder="输入自定义理由"
              @escape="cancelCustomReason"
            )
            Button.h-8(size="sm" @click="cancelCustomReason" variant="ghost") 取消
    div(class="xs:w-1/2 sm:w-1/2 lg:w-auto text-left")
      div.gap-y-2
        Button(size="sm" @click="addMoney") 确认加余额
        div
          .flex.items-center.gap-x-1.text-xs
            Checkbox.h-4.w-4.shrink-0(v-model="addMoneyOpt.is_payment" :id="fieldId('is-payment')" :disabled="addMoneyOpt.amount < 1")
            label.clickable.unselectable(:for="fieldId('is-payment')" :class="{ 'opacity-50': addMoneyOpt.amount <= 1 }") 银行转账
        div(v-if="!addMoneyOpt.is_payment")
          .flex.items-center.gap-x-1.text-xs
            Checkbox.h-4.w-4.shrink-0(v-model="addMoneyOpt.do_not_record" :id="fieldId('do-not-record')")
            label.clickable.unselectable(:for="fieldId('do-not-record')") 不计入账单
        div(v-if="addMoneyOpt.amount < 0")
          .flex.items-center.gap-x-1.text-xs
            Checkbox.h-4.w-4.shrink-0(v-model="addMoneyOpt.allow_negative" :id="fieldId('allow-negative')")
            label.clickable.unselectable(:for="fieldId('allow-negative')") 允许负数
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '~/utils/sonner'
const $utils = useUtils()
const showPreview = ref(true)
const showCustomReason = ref(false)
const selectedReason = ref('')
const customReasonInput = ref(null)
const compId = useId()
const fieldId = (name: string) => `${compId}-${name}`

const addMoneyOpt = ref({
  amount: '' as any,
  do_not_record: false,
  // fapiao_payment: false,
  reason: '',
  is_payment: false,
  currency: 'eur',
  transfer_amount: null as any,
  allow_negative: false,
})
const user = useUser()
const balance = computed(() => user?.money)
const newBalance = computed(() => {
  let ret = Math.floor(Math.round((balance.value || 0) * 100) + Math.round(addMoneyOpt.value.amount * 100)) / 100
  if (ret < 0 && !addMoneyOpt.value.allow_negative) {
    ret = 0
  }
  return ret
})

// Watch for reason selection changes
watch(selectedReason, (newVal) => {
  if (newVal === 'custom') {
    showCustomReason.value = true
    addMoneyOpt.value.reason = ''
    // Auto-focus the custom reason input after it's rendered
    nextTick(() => {
      console.log('Focusing custom reason input', customReasonInput.value)
      customReasonInput.value?.$el?.focus()
    })
  } else if (newVal) {
    showCustomReason.value = false
    addMoneyOpt.value.reason = newVal
  }
})

// Function to cancel custom reason and go back to dropdown
function cancelCustomReason() {
  showCustomReason.value = false
  selectedReason.value = ''
  addMoneyOpt.value.reason = ''
}

watch(() => addMoneyOpt.value.is_payment, () => {
  if (addMoneyOpt.value.is_payment) {
    addMoneyOpt.value.reason = 'Bank Transfer'
    selectedReason.value = ''
    showCustomReason.value = false
  } else if (addMoneyOpt.value.reason === 'Bank Transfer') {
    addMoneyOpt.value.reason = ''
  }
})

async function addMoney() {
  const opt = {
    user_id: user.id!,
    ...addMoneyOpt.value,
  }
  const oldBalance = user.money
  const data = await useAdmin.addMoney(opt)
  user.money = data.result.money
  showPreview.value = false
  toast({ title: `余额已增加 ${addMoneyOpt.value.amount}`, description: `当前余额 ${oldBalance} -> ${balance.value}` });
}

watch(addMoneyOpt, () => {
  console.log('Changed.')
  showPreview.value = true
}, { deep: true })
</script>
